import { concat } from 'lodash';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthDataProvider } from 'src/components/authProvider';
import {
  useGetComponentsWithoutInclude,
  useGetComponentsWithPagination,
  useGetComponentTypesWithPagination,
  useParamQuery,
  useUpdateComponentTags,
} from 'src/hooks';
import { useGetAllComponentTags } from 'src/hooks/api/componentTags/useGetAllComponentTags';
import { useUpdateComponentTag } from 'src/hooks/api/componentTags/useUpdateComponentTag';
import { IComponentType } from 'src/interfaces';
import { formatQuery } from 'src/utils';

export const useEditComponentTagPage = (componentTagId: number) => {
  const { onChangeParams } = useParamQuery();
  const { userRole } = useContext(AuthDataProvider);
  const location = useLocation();

  const {
    data: persistedSelectedComponents,
    isLoading: isSelectedDataLoadComplete,
    isSuccess: isSelectedDataSuccess,
  } = useGetComponentsWithoutInclude([
    `component_tag_id=${componentTagId}`,
    'sort=-created_at',
  ]);

  const {
    data: selectedPageData,
    isLoading: isSelectedPageDataLoadComplete,
    isSuccess: isSelectedPageDataSuccess,
  } = useGetComponentsWithoutInclude(
    concat(formatQuery(location.search), [
      `component_tag_id=${componentTagId}`,
      'sort=-created_at',
    ]),
  );

  const {
    data,
    isLoading: isLoadingComponents,
    error: errorLoadingComponents,
    isSuccess: isComponentSuccess,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetComponentsWithPagination(
    concat(formatQuery(location.search), [
      selectedPageData?.length
        ? `id.notlist=${selectedPageData.map((item) => item.id).join('|')}`
        : '',
      'sort=-created_at',
    ]),
    {
      enabled: isSelectedPageDataSuccess === true,
    },
  );

  const [componentTypesPage, setComponentTypesPage] = useState({
    data: [] as IComponentType[],
    page: 1,
    totalPages: 1,
  });

  const {
    data: componentTypesData,
    isLoading: isLoadingComponentTypes,
    error: errorLoadingComponentTypes,
  } = useGetComponentTypesWithPagination([`page=${componentTypesPage.page}`], {
    staleTime: Infinity,
  });

  const { data: componentTags = [], isLoading: isLoadingComponentTags } =
    useGetAllComponentTags(userRole);

  const selectedComponentTag = componentTags.find(
    (componentTag) => componentTag.id === componentTagId,
  );

  const updateComponentTag = useUpdateComponentTag();
  const updateComponents = useUpdateComponentTags();

  const getNextComponentPage = useCallback(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage]);

  useEffect(() => {
    if (componentTypesData && componentTypesData.data) {
      setComponentTypesPage({
        ...componentTypesPage,
        data: [...componentTypesPage.data, ...componentTypesData.data],
        totalPages: componentTypesData.totalPages,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentTypesData, setComponentTypesPage]);

  return {
    data,
    persistedSelectedComponents,
    isSelectedDataLoadComplete,
    isSelectedDataSuccess,
    selectedPageData,
    isSelectedPageDataLoadComplete,
    isSelectedPageDataSuccess,
    isComponentSuccess,
    componentTypesPage,
    getNextComponentPage,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    isLoadingComponentTags,
    errorLoadingComponents,
    isLoadingComponentTypes,
    errorLoadingComponentTypes,
    isLoadingComponents,
    onChangeParams,
    updateComponentTag,
    updateComponents,
    selectedComponentTag,
  };
};
